<script setup>
import Bugsnag from '@bugsnag/js';
import { Icon } from '@iconify/vue';
import { computed } from 'vue';

import '@/assets/icons/index.js';

const props = defineProps({
    name: {
        /** @type import('vue').PropType<IconName> */
        type: String,
        required: true,
    },
    /**
     * @type {'light' | 'bold'}
     * @default 'light'
     */
    variant: {
        type: String,
        default: 'light',
        validator: (v) => ['light', 'bold'].includes(v),
    },
    size: {
        type: [String, Number],
        default: 20,
    },
    class: {
        type: [String, Array, Object],
        default: '',
    },
});

const iconName = computed(() => {
    if (!props.name) {
        console.warn('CFIcon component received an empty name prop.', props.name);

        Bugsnag.notify(new Error('CFIcon component received invalid name prop'), (event) => {
            event.addMetadata('component', {
                receivedName: props.name,
                variant: props.variant,
                size: props.size,
            });
        });

        return 'question-circle';
    }

    // If name already includes namespace, use it as-is
    if (props.name.includes(':')) {
        return props.name;
    }

    // Strip -bold suffix if present (normalize the name)
    const baseName = props.name.replace(/-bold$/, '');

    // Append -bold suffix for bold variant
    const iconWithVariant = props.variant === 'bold' ? `${baseName}-bold` : baseName;

    return `custom:${iconWithVariant}`;
});

const iconSize = computed(() => {
    return typeof props.size === 'number' ? `${props.size}px` : props.size;
});
</script>

<template>
    <Icon
        :icon="iconName"
        :width="iconSize"
        :height="iconSize"
        :class="[props.class, 'fill-none']"
    />
</template>
